import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, BarChart3, PieChart,
  Calendar, AlertTriangle, CheckCircle, Clock, ArrowUp,
  ArrowDown, Minus, FileText, Download, RefreshCw
} from 'lucide-react';

interface PLData {
  period: { from: string; to: string };
  sales: {
    amount: number;
    tax: number;
    subtotal: number;
    count: number;
  };
  cost_of_sales: {
    actual: number;
    theoretical: number;
    amount: number;
  };
  gross_profit: {
    amount: number;
    rate: number;
  };
  expenses: {
    amount: number;
    details: Array<{
      code: string;
      name: string;
      amount: number;
    }>;
    categories: number;
  };
  operating_profit: {
    amount: number;
    rate: number;
  };
  net_profit: {
    amount: number;
    rate: number;
  };
  analysis?: {
    daily_sales: Array<{ date: string; daily_sales: number }>;
    category_sales: Array<{ category: string; sales: number }>;
    average_daily_sales: number;
    cost_rate: number;
    expense_rate: number;
  };
}

interface ComparisonData {
  current: any;
  previous: any;
  changes: any;
}

const ProfitLossDashboard: React.FC = () => {
  const [plData, setPlData] = useState<PLData | null>(null);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'pl' | 'comparison' | 'trend'>('pl');

  useEffect(() => {
    fetchPLData();
  }, [selectedPeriod, selectedYear, selectedMonth]);

  const fetchPLData = async () => {
    setLoading(true);
    try {
      let params = '';
      if (selectedPeriod === 'month') {
        params = `?year=${selectedYear}&month=${selectedMonth}`;
      } else if (selectedPeriod === 'year') {
        params = `?year=${selectedYear}`;
      }

      // P/Lデータ取得
      const plResponse = await fetch(`/api/profit-loss/report${params}`);
      const plResult = await plResponse.json();
      if (plResult.success) {
        setPlData(plResult.data);
      }

      // 比較データ取得（月次の場合のみ）
      if (selectedPeriod === 'month') {
        const compResponse = await fetch(`/api/profit-loss/comparison?year=${selectedYear}&month=${selectedMonth}`);
        const compResult = await compResponse.json();
        if (compResult.success) {
          setComparisonData(compResult.data);
        }
      }
    } catch (err) {
      console.error('P/Lデータ取得エラー:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `¥${Math.abs(amount).toLocaleString()}`;
  };

  const getChangeIcon = (amount: number) => {
    if (amount > 0) return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (amount < 0) return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getRateColor = (rate: number) => {
    if (rate >= 20) return 'text-green-600';
    if (rate >= 10) return 'text-yellow-600';
    if (rate >= 0) return 'text-orange-600';
    return 'text-red-600';
  };

  const handleExport = () => {
    // CSV出力機能（実装省略）
    alert('P/Lデータをエクスポートします');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* ヘッダー */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            損益計算書（P/L）
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('pl')}
              className={`px-4 py-2 rounded-lg ${
                viewMode === 'pl' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'
              }`}
            >
              P/L
            </button>
            <button
              onClick={() => setViewMode('comparison')}
              className={`px-4 py-2 rounded-lg ${
                viewMode === 'comparison' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'
              }`}
            >
              前期比較
            </button>
            <button
              onClick={() => setViewMode('trend')}
              className={`px-4 py-2 rounded-lg ${
                viewMode === 'trend' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'
              }`}
            >
              トレンド
            </button>
          </div>
        </div>
      </div>

      {/* 期間選択 */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex gap-4 items-center">
          <select
            className="px-4 py-2 border rounded-lg"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="month">月次</option>
            <option value="year">年次</option>
          </select>

          <select
            className="px-4 py-2 border rounded-lg"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {[2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}年</option>
            ))}
          </select>

          {selectedPeriod === 'month' && (
            <select
              className="px-4 py-2 border rounded-lg"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                <option key={month} value={month}>{month}月</option>
              ))}
            </select>
          )}

          <button
            onClick={fetchPLData}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            更新
          </button>

          <div className="flex-1" />

          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            エクスポート
          </button>
        </div>
      </div>

      {plData && viewMode === 'pl' && (
        <>
          {/* KPIカード */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">売上高</span>
                <DollarSign className="w-5 h-5 text-blue-500" />
              </div>
              <div className="text-2xl font-bold">{formatCurrency(plData.sales.amount)}</div>
              <div className="text-xs text-gray-400 mt-1">
                {plData.sales.count}件
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">売上総利益</span>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-2xl font-bold">{formatCurrency(plData.gross_profit.amount)}</div>
              <div className={`text-xs mt-1 ${getRateColor(plData.gross_profit.rate)}`}>
                利益率 {plData.gross_profit.rate}%
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">営業利益</span>
                <BarChart3 className="w-5 h-5 text-purple-500" />
              </div>
              <div className="text-2xl font-bold">{formatCurrency(plData.operating_profit.amount)}</div>
              <div className={`text-xs mt-1 ${getRateColor(plData.operating_profit.rate)}`}>
                利益率 {plData.operating_profit.rate}%
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">当期純利益</span>
                {plData.net_profit.amount >= 0 ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                )}
              </div>
              <div className={`text-2xl font-bold ${plData.net_profit.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(plData.net_profit.amount)}
              </div>
              <div className={`text-xs mt-1 ${getRateColor(plData.net_profit.rate)}`}>
                利益率 {plData.net_profit.rate}%
              </div>
            </div>
          </div>

          {/* P/L詳細 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* P/L表 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold mb-4">損益計算書</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="font-medium">売上高</span>
                  <span className="font-bold text-lg">{formatCurrency(plData.sales.amount)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-600">売上原価</span>
                  <span className="text-red-600">-{formatCurrency(plData.cost_of_sales.amount)}</span>
                </div>

                <div className="flex justify-between items-center py-2 bg-gray-50 px-2 rounded">
                  <span className="font-medium">売上総利益</span>
                  <div className="text-right">
                    <div className="font-bold">{formatCurrency(plData.gross_profit.amount)}</div>
                    <div className="text-xs text-gray-500">({plData.gross_profit.rate}%)</div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-600">販売費及び一般管理費</span>
                  <span className="text-red-600">-{formatCurrency(plData.expenses.amount)}</span>
                </div>

                <div className="flex justify-between items-center py-2 bg-blue-50 px-2 rounded">
                  <span className="font-medium">営業利益</span>
                  <div className="text-right">
                    <div className="font-bold text-blue-600">{formatCurrency(plData.operating_profit.amount)}</div>
                    <div className="text-xs text-gray-500">({plData.operating_profit.rate}%)</div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                  <span className="font-bold text-lg">当期純利益</span>
                  <div className="text-right">
                    <div className={`font-bold text-xl ${plData.net_profit.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(plData.net_profit.amount)}
                    </div>
                    <div className="text-xs text-gray-500">({plData.net_profit.rate}%)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 経費内訳 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold mb-4">経費内訳</h2>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {plData.expenses.details.map((expense, index) => (
                  <div key={index} className="flex justify-between items-center py-2 hover:bg-gray-50 px-2 rounded">
                    <div>
                      <div className="font-medium">{expense.name}</div>
                      <div className="text-xs text-gray-500">{expense.code}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{formatCurrency(expense.amount)}</div>
                      <div className="text-xs text-gray-500">
                        {plData.expenses.amount > 0
                          ? ((expense.amount / plData.expenses.amount) * 100).toFixed(1)
                          : 0}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="font-bold">経費合計</span>
                  <span className="font-bold text-lg">{formatCurrency(plData.expenses.amount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 分析指標 */}
          {plData.analysis && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-500 mb-1">平均日商</div>
                <div className="text-xl font-bold">{formatCurrency(plData.analysis.average_daily_sales)}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-500 mb-1">原価率</div>
                <div className={`text-xl font-bold ${getRateColor(100 - parseFloat(plData.analysis.cost_rate))}`}>
                  {plData.analysis.cost_rate}%
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-500 mb-1">経費率</div>
                <div className={`text-xl font-bold ${getRateColor(100 - parseFloat(plData.analysis.expense_rate))}`}>
                  {plData.analysis.expense_rate}%
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {comparisonData && viewMode === 'comparison' && (
        /* 前期比較表示 */
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">前期比較</h2>
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">項目</th>
                <th className="text-right py-2">当期</th>
                <th className="text-right py-2">前期</th>
                <th className="text-right py-2">増減額</th>
                <th className="text-right py-2">増減率</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-3">売上高</td>
                <td className="text-right font-semibold">{formatCurrency(comparisonData.current.sales)}</td>
                <td className="text-right">{formatCurrency(comparisonData.previous.sales)}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {getChangeIcon(comparisonData.changes.sales.amount)}
                    {formatCurrency(comparisonData.changes.sales.amount)}
                  </div>
                </td>
                <td className={`text-right ${comparisonData.changes.sales.rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comparisonData.changes.sales.rate}%
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-3">売上原価</td>
                <td className="text-right font-semibold">{formatCurrency(comparisonData.current.cost_of_sales)}</td>
                <td className="text-right">{formatCurrency(comparisonData.previous.cost_of_sales)}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {getChangeIcon(comparisonData.changes.cost_of_sales.amount)}
                    {formatCurrency(comparisonData.changes.cost_of_sales.amount)}
                  </div>
                </td>
                <td className={`text-right ${comparisonData.changes.cost_of_sales.rate <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comparisonData.changes.cost_of_sales.rate}%
                </td>
              </tr>
              <tr className="border-b bg-gray-50">
                <td className="py-3 font-medium">売上総利益</td>
                <td className="text-right font-bold">{formatCurrency(comparisonData.current.gross_profit)}</td>
                <td className="text-right font-semibold">{formatCurrency(comparisonData.previous.gross_profit)}</td>
                <td className="text-right font-semibold">
                  <div className="flex items-center justify-end gap-1">
                    {getChangeIcon(comparisonData.changes.gross_profit.amount)}
                    {formatCurrency(comparisonData.changes.gross_profit.amount)}
                  </div>
                </td>
                <td className={`text-right font-semibold ${comparisonData.changes.gross_profit.rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comparisonData.changes.gross_profit.rate}%
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-3">経費</td>
                <td className="text-right font-semibold">{formatCurrency(comparisonData.current.expenses)}</td>
                <td className="text-right">{formatCurrency(comparisonData.previous.expenses)}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {getChangeIcon(comparisonData.changes.expenses.amount)}
                    {formatCurrency(comparisonData.changes.expenses.amount)}
                  </div>
                </td>
                <td className={`text-right ${comparisonData.changes.expenses.rate <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comparisonData.changes.expenses.rate}%
                </td>
              </tr>
              <tr className="bg-blue-50">
                <td className="py-3 font-bold">営業利益</td>
                <td className="text-right font-bold text-lg">{formatCurrency(comparisonData.current.operating_profit)}</td>
                <td className="text-right font-bold">{formatCurrency(comparisonData.previous.operating_profit)}</td>
                <td className="text-right font-bold">
                  <div className="flex items-center justify-end gap-1">
                    {getChangeIcon(comparisonData.changes.operating_profit.amount)}
                    {formatCurrency(comparisonData.changes.operating_profit.amount)}
                  </div>
                </td>
                <td className={`text-right font-bold text-lg ${comparisonData.changes.operating_profit.rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {comparisonData.changes.operating_profit.rate}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProfitLossDashboard;